import React, { useState, useContext, useEffect, useMemo, useCallback } from "react";
import { TableContext } from "../../context";
import "../../App.css";
import './SiteView.css'
import Papa from "papaparse";
import { DateTime } from 'luxon';
import axios from 'axios';

import { McLoadingIndicator } from "@maersk-global/mds-react-wrapper/components-core/mc-loading-indicator";
import {
    McTable,
    McButton,
    McPagination,
    McInput,
    McSelect,
    McOption,
    McIcon
} from "@maersk-global/mds-react-wrapper";
import { McTabBar } from "@maersk-global/mds-react-wrapper/components-core/mc-tab-bar";
import { McTab } from "@maersk-global/mds-react-wrapper/components-core/mc-tab";
import { McTooltip } from "@maersk-global/mds-react-wrapper/components-core/mc-tooltip";
import { McSegmentedControl } from "@maersk-global/mds-react-wrapper/components-core/mc-segmented-control";
import { McSegmentedControlItem } from "@maersk-global/mds-react-wrapper/components-core/mc-segmented-control-item";
import FilterButton from "../FilterButton";

// ============================================================================
// CONSTANTS
// ============================================================================

const SCOPE_TABS = ['Regional', 'Global', 'Unmapped'];

const TABLE_COLUMNS = [
    { id: "changeNumber", label: "Change Number", sortable: true },
    { id: "configItem", label: "Configuration Item", sortable: true },
    { id: "location", label: "Location", sortable: true },
    { id: "risk", label: "Risk", sortable: true },
    { id: "shortDescription", label: "Short Description", sortable: true, nowrap: false },
    { id: "plannedStartDate", label: "Planned Start Date", sortable: true },
    { id: "plannedEndDate", label: "Planned End Date", sortable: true },
    { id: "state", label: "State", sortable: true },
    { id: "type", label: "Type", sortable: true },
    { id: "assignmentGroup", label: "Assignment Group", sortable: true }
];

const FILTER_COLUMNS = [
    { id: "Change Number", label: "Change Number" },
    { id: "Configuration Item", label: "Configuration Item" },
    { id: "Location", label: "Location" },
    { id: "Risk", label: "Risk" },
    { id: "State", label: "State" },
    { id: "Type", label: "Type" },
    { id: "Short Description", label: "Short Description" },
    { id: "Planned Start Date", label: "Planned Start Date" },
    { id: "Planned End Date", label: "Planned End Date" },
    { id: "Assignment Group", label: "Assignment Group" }
];

const RECORDS_PER_PAGE = 50;

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

const parseLocationFromPath = (path) => {
    // Path format: "Region/Country/City/Site" or "Region/Country" or "Region" or "Global"
    if (!path) return { region: null, country: null, city: null, site: null };
    
    const parts = path.split('/');
    
    // Return parsed parts - even if only region is present
    return {
        region: parts[0] || null,
        country: parts[1] || null,
        city: parts[2] || null,
        site: parts[3] || null  // Site is at index 3 (Region/Country/City/Site)
    };
};

const getRiskColor = (risk) => {
    switch (risk?.toLowerCase()) {
        case 'low': return '#0077b2';
        case 'negligible': return '#666';
        case 'significant': return '#e68a00';
        case 'high': return '#e75858';
        default: return '#666';
    }
};

const getStateColor = (state) => {
    switch (state?.toLowerCase()) {
        case 'review': return '#d8a000';
        case 'ongoing': return '#0077b2';
        case 'completed': return '#00786b';
        case 'cancelled': return '#e75858';
        default: return '#666';
    }
};

const sortArrayByProperty = (data, property, order = 'ascending') => {
    return [...data].sort((a, b) => {
        let comparison = 0;
        if (property === "plannedStartDate" || property === "plannedEndDate") {
            const dateA = DateTime.fromFormat(a[property] || '', 'dd-MM-yyyy HH:mm:ss');
            const dateB = DateTime.fromFormat(b[property] || '', 'dd-MM-yyyy HH:mm:ss');
            if (dateA < dateB) comparison = -1;
            else if (dateA > dateB) comparison = 1;
        } else if (property === "changeNumber") {
            const numA = parseInt((a[property] || '').replace(/\D/g, ""), 10);
            const numB = parseInt((b[property] || '').replace(/\D/g, ""), 10);
            comparison = !isNaN(numA) && !isNaN(numB) 
                ? numA - numB 
                : String(a[property] || '').localeCompare(String(b[property] || ''));
        } else {
            const valA = a[property] || '';
            const valB = b[property] || '';
            if (valA > valB) comparison = 1;
            else if (valA < valB) comparison = -1;
        }
        return order === 'ascending' ? comparison : comparison * -1;
    });
};

// ============================================================================
// STYLES
// ============================================================================

const styles = {

    container: {
        display: 'flex',
        flexDirection: 'column',
        height: 'calc(100vh - 280px)',
        minHeight: '500px',
        overflow: 'hidden'
    },
    tabBarWrapper: {
        display: 'flex',
        alignItems: 'center',
        margin: '5px',
        gap: '16px'
    },
    segmentedControlContainer: {
        display: 'flex',
        alignItems: 'center'
    },
    tabBarContent: {
        // No flex - tabs take natural width
    },
    topBar: {
        padding: '16px 20px',
        backgroundColor: '#fff',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '16px'
    },
    leftSection: {
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        flex: 1
    },
    rightSection: {
        display: 'flex',
        alignItems: 'center',
        gap: '12px'
    },
    mainContent: {
        display: 'flex',
        flex: 1,
        overflow: 'hidden'
    },
    sidebar: {
        width: '280px',
        minWidth: '280px',
        borderRight: '1px solid #e0e0e0',
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: '#fafafa',
        overflow: 'hidden'
    },
    sidebarHeader: {
        padding: '16px',
        borderBottom: '1px solid #e0e0e0',
        backgroundColor: '#fff'
    },
    sidebarFilters: {
        padding: '12px 16px',
        borderBottom: '1px solid #e0e0e0',
        backgroundColor: '#fff',
        display: 'flex',
        flexDirection: 'column',
        gap: '12px'
    },
    sidebarNav: {
        flex: 1,
        overflowY: 'auto',
        overflowX: 'hidden'
    },
    sectionHeader: {
        padding: '12px 16px 8px',
        fontSize: '11px',
        fontWeight: '600',
        color: '#666',
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
        backgroundColor: '#f5f5f5',
        borderBottom: '1px solid #e8e8e8',
        position: 'sticky',
        top: 0,
        zIndex: 1
    },
    navItem: (isSelected) => ({
        padding: '10px 16px',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        backgroundColor: isSelected ? '#e6f3ff' : 'transparent',
        borderLeft: isSelected ? '3px solid #0077b2' : '3px solid transparent',
        transition: 'background-color 0.15s ease',
        fontSize: '14px',
        color: isSelected ? '#0077b2' : '#333',
        fontWeight: isSelected ? '600' : '400'
    }),
    navItemContent: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        flex: 1,
        minWidth: 0
    },
    navItemLabel: {
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap'
    },
    badge: (color = '#0077b2') => ({
        backgroundColor: color,
        color: 'white',
        borderRadius: '12px',
        padding: '2px 8px',
        fontSize: '11px',
        fontWeight: '600',
        minWidth: '24px',
        textAlign: 'center',
        flexShrink: 0
    }),
    contentPanel: {
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden'
    },
    contentHeader: {
        padding: '16px 20px',
        borderBottom: '1px solid #e0e0e0',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        backgroundColor: '#fff',
        flexShrink: 0
    },
    contentTitle: {
        margin: 0,
        fontSize: '18px',
        fontWeight: '600',
        color: '#333'
    },
    contentSubtitle: {
        fontSize: '13px',
        color: '#666'
    },
    headerActions: {
        display: 'flex',
        alignItems: 'center',
        gap: '12px'
    },
    tableContainer: {
        flex: 1,
        overflow: 'auto'
    },
    pagination: {
        padding: '12px 20px',
        display: 'flex',
        justifyContent: 'center',
        borderTop: '1px solid #e0e0e0',
        backgroundColor: '#fff',
        flexShrink: 0
    },
    emptyState: {
        textAlign: 'center',
        padding: '3rem',
        fontSize: '1.1rem',
        color: '#666'
    }
    
};

// ============================================================================
// COMPONENT
// ============================================================================

export default function SiteRegionView({ onOpenDrawer, showSiteView, setShowSiteView }) {
    const { selectedTime, search, searchType } = useContext(TableContext);
    
    // State
    const [loading, setLoading] = useState(false);
    const [siteChangesData, setSiteChangesData] = useState([]);
    const [activeScope, setActiveScope] = useState(0); // 0: Regional, 1: Global, 2: Unmapped
    const [selectedSiteType, setSelectedSiteType] = useState('all');
    const [siteSearch, setSiteSearch] = useState('');
    const [selectedNavItem, setSelectedNavItem] = useState({ type: 'all', label: 'All Locations' });
    const [expandedRegions, setExpandedRegions] = useState(new Set());
    const [expandedCountries, setExpandedCountries] = useState(new Set());
    const [activeFilters, setActiveFilters] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [sortColumn, setSortColumn] = useState("plannedStartDate");
    const [sortDirection, setSortDirection] = useState("descending");
    const [headingText, setHeadingText] = useState('Ongoing Changes');

    // ========================================================================
    // API DATA FETCHING
    // ========================================================================

    const fetchSiteViewData = useCallback(async () => {
        if (!selectedTime?.start || !selectedTime?.end) return;
        
        setLoading(true);
        try {
            // Format time to match API requirements (yyyy-MM-dd HH:mm:ss)
            const formatDateTime = (date) => {
                const dt = DateTime.fromJSDate(date);
                return dt.toFormat('yyyy-MM-dd HH:mm:ss');
            };

            const payload = {
                start_time: formatDateTime(selectedTime.start),
                end_time: formatDateTime(selectedTime.end)
            };

            // Add app_name to payload if application search is active
            if (search && searchType === 'application') {
                payload.app_name = search.trim();
            }

            const response = await axios.post(
                `${process.env.REACT_APP_BACKEND_URI}/api/change_data/getSNOWChangesSiteView`,
                payload,
                {
                    headers: {
                        Authorization: "Bearer " + localStorage.getItem("jwt_token"),
                    }
                }
            );

            const { data: responseData, status } = response.data || {};

            if (status === 200 && responseData) {
                // Combine all change types from the response
                const allChanges = [
                    ...(responseData.global_changes || []),
                    ...(responseData.unmapped_changes || [])
                ];
                
                // Parse flat regional_changes structure
                if (responseData.regional_changes) {
                    Object.entries(responseData.regional_changes).forEach(([key, changes]) => {
                        // If key is "null", treat as unmapped, otherwise add to regional
                        if (Array.isArray(changes)) {
                            if (key === "null") {
                                // Add null key changes to unmapped
                                allChanges.push(...changes.map(change => ({
                                    ...change,
                                    location_name: change.location_name || 'Unmapped',
                                    location_type: change.location_type || 'Unknown',
                                    path: change.path || 'Unmapped'
                                })));
                            } else {
                                allChanges.push(...changes);
                            }
                        }
                    });
                }
                
                setSiteChangesData(allChanges);
            } else {
                setSiteChangesData([]);
            }
        } catch (error) {
            console.error("Error fetching site view data:", error);
            setSiteChangesData([]);
        } finally {
            setLoading(false);
        }
    }, [selectedTime, search, searchType]);

    // Fetch data when component mounts or dependencies change
    useEffect(() => {
        if (showSiteView) {
            fetchSiteViewData();
        }
    }, [showSiteView, fetchSiteViewData]);

    // ========================================================================
    // DATA PROCESSING
    // ========================================================================

    // Normalize raw data with region/country/site info
    const normalizedData = useMemo(() => {
        return siteChangesData.map(item => {
            const location = item.location_name || item.Location || '';
            const path = item.path || '';
            const locationType = item.location_type?.toLowerCase() || '';
            
            // Parse region, country, city, and site from path
            const { region, country, city, site } = parseLocationFromPath(path);
            
            // Determine scope
            let scope = 'unmapped';
            if (path === 'Unmapped' || locationType === 'unmapped' || location === 'Unmapped') {
                scope = 'unmapped';
            } else if (locationType === 'global' || location.toLowerCase() === 'global' || path.toLowerCase() === 'global') {
                scope = 'global';
            } else if (region && region !== 'Unmapped') {
                // Mark as regional if region exists (even without country/city/site)
                scope = 'regional';
            } else if (location && location.trim() !== '') {
                scope = 'unmapped';
            }

            return {
                ...item,
                "Change Number": item["Change Number"] || item.change_number || '',
                "Configuration Item": item["Configuration Item"] || item.configuration_item || '',
                "Location": location,
                "Risk": item.Risk || item.risk || '',
                "Short Description": item["Short Description"] || item.short_description || '',
                "Planned Start Date": item["Planned Start Date"] || item.planned_start_date || '',
                "Planned End Date": item["Planned End Date"] || item.planned_end_date || '',
                "State": item.State || item.state || '',
                "Type": item.Type || item.type || '',
                "Assignment Group": item["Assignment Group"] || item.assignment_group || '',
                "Requested By": item["Requested By"] || item.requested_by || '',
                "Close Code": item["Close Code"] || item.close_code || '',
                "Category": item["Category"] || item.category || '',
                _scope: scope,
                _region: region,
                _country: country,
                _city: city,
                _site: site || (location.includes('-') ? location : null),
                _siteType: locationType
            };
        });
    }, [siteChangesData]);

    // Generate dynamic site types from regional changes only
    const SITE_TYPES = useMemo(() => {
        const uniqueTypes = new Set();
        normalizedData.forEach(item => {
            // Only include location_type from regional changes
            if (item._scope === 'regional') {
                const locationType = item.location_type;
                if (locationType && locationType.trim() !== '') {
                    uniqueTypes.add(locationType);
                }
            }
        });

        const types = Array.from(uniqueTypes).sort().map(type => ({
            value: type.toLowerCase().replace(/\s+/g, '-'),
            label: type
        }));
        return [
            { value: 'all', label: 'All Sites' },
            ...types
        ];
    }, [normalizedData]);



    // Get data filtered by current scope tab
    const scopeFilteredData = useMemo(() => {
        const scopeMap = ['regional', 'global', 'unmapped'];
        const currentScope = scopeMap[activeScope];
        return normalizedData.filter(item => item._scope === currentScope);
    }, [normalizedData, activeScope]);

    // Filter by site type (only applied on regional scope)
    const siteTypeFilteredData = useMemo(() => {
        // Only apply site type filter on Regional tab (activeScope === 0)
        if (activeScope !== 0 || selectedSiteType === 'all') return scopeFilteredData;
        
        return scopeFilteredData.filter(item => {
            const itemType = item._siteType?.toLowerCase().replace(/\s+/g, '-') || '';
            return itemType === selectedSiteType;
        });
    }, [scopeFilteredData, selectedSiteType, activeScope]);

    // Build hierarchical navigation structure for Regional tab
    const navigationStructure = useMemo(() => {
        const structure = {};
        
        siteTypeFilteredData.forEach(item => {
            if (item._region) {
                if (!structure[item._region]) {
                    structure[item._region] = { count: 0, countries: {} };
                }
                structure[item._region].count++;
                
                if (item._country) {
                    if (!structure[item._region].countries[item._country]) {
                        structure[item._region].countries[item._country] = { count: 0, cities: {} };
                    }
                    structure[item._region].countries[item._country].count++;
                    if (item._city && item._site) {
                        if (!structure[item._region].countries[item._country].cities[item._city]) {
                            structure[item._region].countries[item._country].cities[item._city] = { count: 0, sites: {} };
                        }
                        structure[item._region].countries[item._country].cities[item._city].count++;
                        
                        if (!structure[item._region].countries[item._country].cities[item._city].sites[item._site]) {
                            structure[item._region].countries[item._country].cities[item._city].sites[item._site] = { count: 0 };
                        }
                        structure[item._region].countries[item._country].cities[item._city].sites[item._site].count++;
                    }
                }
            }
        });

        return structure;
    }, [siteTypeFilteredData]);

    // Filter navigation items based on search
    const filteredNavigation = useMemo(() => {
        if (!siteSearch.trim()) return navigationStructure;
        
        const searchLower = siteSearch.toLowerCase();
        const filtered = {};
        const regionsToExpand = new Set();
        const countriesToExpand = new Set();

        Object.entries(navigationStructure).forEach(([region, regionData]) => {
            const regionMatches = region.toLowerCase().includes(searchLower);
            const matchingCountries = {};

            Object.entries(regionData.countries || {}).forEach(([country, countryData]) => {
                const countryMatches = country.toLowerCase().includes(searchLower);
                const matchingCities = {};

                // Iterate through cities
                Object.entries(countryData.cities || {}).forEach(([city, cityData]) => {
                    const cityMatches = city.toLowerCase().includes(searchLower);
                    const matchingSites = {};

                    // Iterate through sites
                    Object.entries(cityData.sites || {}).forEach(([site, siteData]) => {
                        if (site.toLowerCase().includes(searchLower)) {
                            matchingSites[site] = siteData;
                            regionsToExpand.add(region);
                            countriesToExpand.add(`${region}::${country}`);
                            countriesToExpand.add(`${region}::${country}::${city}`);
                        }
                    });

                    if (Object.keys(matchingSites).length > 0 || cityMatches) {
                        matchingCities[city] = { 
                            ...cityData, 
                            sites: Object.keys(matchingSites).length > 0 ? matchingSites : cityData.sites 
                        };
                        if (cityMatches) {
                            regionsToExpand.add(region);
                            countriesToExpand.add(`${region}::${country}`);
                        }
                    }
                });

                if (Object.keys(matchingCities).length > 0 || countryMatches) {
                    matchingCountries[country] = { 
                        ...countryData, 
                        cities: Object.keys(matchingCities).length > 0 ? matchingCities : countryData.cities 
                    };
                    if (countryMatches) {
                        regionsToExpand.add(region);
                    }
                }
            });

            if (Object.keys(matchingCountries).length > 0 || regionMatches) {
                filtered[region] = { 
                    ...regionData, 
                    countries: Object.keys(matchingCountries).length > 0 ? matchingCountries : regionData.countries 
                };
            }
        });
        
        if (Object.keys(filtered).length > 0) {
            setExpandedRegions(regionsToExpand);
            setExpandedCountries(countriesToExpand);
        }

        return filtered;
    }, [navigationStructure, siteSearch]);

    // Get filtered data based on selected navigation item
    const filteredData = useMemo(() => {
        let data = [...siteTypeFilteredData];

        // Apply location filter from sidebar
        if (activeScope === 0 && selectedNavItem.type !== 'all') {
            switch (selectedNavItem.type) {
                case 'region':
                    // Show all changes in this region
                    data = data.filter(item => item._region === selectedNavItem.region);
                    break;
                case 'country':
                    // Show all changes in this country (country-level + all sites within)
                    data = data.filter(item => 
                        item._region === selectedNavItem.region && 
                        item._country === selectedNavItem.country
                    );
                    break;
                case 'city':
                    // Show all changes in this city
                    data = data.filter(item => 
                        item._region === selectedNavItem.region && 
                        item._country === selectedNavItem.country &&
                        item._city === selectedNavItem.city
                    );
                    break;
                case 'site':
                    // Show changes for this specific site
                    data = data.filter(item => item._site === selectedNavItem.site);
                    break;
                default:
                    break;
            }
        }

        // Apply global search
        if (search && searchType === 'global') {
            const terms = search.split(',').map(term => term.trim().toLowerCase()).filter(Boolean);
            if (terms.length > 0) {
                data = data.filter(item => {
                    return terms.some(term => {
                        return Object.values(item).some(value => {
                            if (value === null || value === undefined) return false;
                            return String(value).toLowerCase().includes(term);
                        });
                    });
                });
            }
        }

        // Apply advanced filters
        if (activeFilters?.length > 0) {
            data = data.filter(item => {
                return activeFilters.every(filter => {
                    const itemValue = item[filter.field];
                    const filterValue = filter.value.toLowerCase();
                    if (itemValue === undefined || itemValue === null) return false;
                    const itemString = String(itemValue).toLowerCase();
                    switch (filter.operator) {
                        case "contains": return itemString.includes(filterValue);
                        case "Does not contain": return !itemString.includes(filterValue);
                        case "equals": return itemString === filterValue;
                        case "Is not": return itemString !== filterValue;
                        case "startsWith": return itemString.startsWith(filterValue);
                        case "endsWith": return itemString.endsWith(filterValue);
                        default: return true;
                    }
                });
            });
        }

        // Sort by date (default)
        data.sort((a, b) => {
            const dateA = DateTime.fromFormat(a["Planned Start Date"] || '', 'dd-MM-yyyy HH:mm:ss');
            const dateB = DateTime.fromFormat(b["Planned Start Date"] || '', 'dd-MM-yyyy HH:mm:ss');
            return dateA < dateB ? 1 : dateA > dateB ? -1 : 0;
        });

        return data;
    }, [siteTypeFilteredData, activeScope, selectedNavItem, search, searchType, activeFilters]);

    // Pagination
    const totalPages = Math.ceil(filteredData.length / RECORDS_PER_PAGE);
    const paginatedData = filteredData.slice(
        (currentPage - 1) * RECORDS_PER_PAGE, 
        currentPage * RECORDS_PER_PAGE
    );

    // Transform data for table
    const transformedData = useMemo(() => {
        let dataToSort = paginatedData.map((item, index) => {
            // Format dates to dd-MM-yyyy HH:mm:ss
            const formatDate = (dateStr) => {
                if (!dateStr) return '';
                const dt = DateTime.fromFormat(dateStr, 'yyyy-MM-dd HH:mm:ss');
                return dt.isValid ? dt.toFormat('dd-MM-yyyy HH:mm:ss') : dateStr;
            };

            return {
                id: `row-${index}`,
                changeNumber: item["Change Number"],
                configItem: item["Configuration Item"],
                location: item["Location"],
                risk: item["Risk"],
                shortDescription: item["Short Description"],
                plannedStartDate: formatDate(item["Planned Start Date"]),
                plannedEndDate: formatDate(item["Planned End Date"]),
                state: item["State"],
                type: item["Type"],
                assignmentGroup: item["Assignment Group"],
                _originalData: item
            };
        });

        if (sortColumn) {
            dataToSort = sortArrayByProperty(dataToSort, sortColumn, sortDirection);
        }

        return dataToSort;
    }, [paginatedData, sortColumn, sortDirection]);

    // ========================================================================
    // EFFECTS
    // ========================================================================

    // Reset page when data changes
    useEffect(() => setCurrentPage(1), [filteredData.length]);

    // Reset navigation when scope changes
    useEffect(() => {
        setSelectedNavItem({ type: 'all', label: 'All Locations' });
        setCurrentPage(1);
    }, [activeScope]);

    // Update heading based on time selection
    useEffect(() => {
        const now = DateTime.now();
        const start = DateTime.fromJSDate(selectedTime.start);
        const end = DateTime.fromJSDate(selectedTime.end);
        const isPast = start < now;
        const isFuture = end > now;
        if (isPast && !isFuture) setHeadingText("Past Changes");
        else if (isFuture && !isPast) setHeadingText("Future Changes");
        else setHeadingText("Ongoing Changes");
    }, [selectedTime]);

    useEffect(() => {
        if (!showSiteView && siteChangesData.length > 0) {
            setSiteChangesData([]);
        }
    }, [showSiteView]);


    const handleScopeTabChange = useCallback((e) => {
        setActiveScope(e.detail);
    }, []);

    const handleSiteTypeChange = useCallback((e) => {
        setSelectedSiteType(e.target.value);
        setSelectedNavItem({ type: 'all', label: 'All Locations' });
        setCurrentPage(1);
    }, []);

    const handleSearchChange = useCallback((e) => {
        setSiteSearch(e.target.value);
    }, []);

    const handlePageChange = useCallback((e) => {
        setCurrentPage(e.detail);
    }, []);

    const handleTableSorted = useCallback(({ detail: { column, direction } }) => {
        setSortColumn(column);
        setSortDirection(direction);
    }, []);

    const handleFilterApply = useCallback((filters) => {
        setActiveFilters(filters);
    }, []);

    const toggleRegion = useCallback((region, e) => {
        e?.stopPropagation();
        setExpandedRegions(prev => {
            const newSet = new Set(prev);
            if (newSet.has(region)) newSet.delete(region);
            else newSet.add(region);
            return newSet;
        });
    }, []);

    const toggleCountry = useCallback((region, country, e, city = null) => {
        e?.stopPropagation();
        const key = city ? `${region}::${country}::${city}` : `${region}::${country}`;
        setExpandedCountries(prev => {
            const newSet = new Set(prev);
            if (newSet.has(key)) newSet.delete(key);
            else newSet.add(key);
            return newSet;
        });
    }, []);

    const selectNavItem = useCallback((type, label, region = null, country = null, city = null, site = null) => {
        setSelectedNavItem({ type, label, region, country, city, site });
        setCurrentPage(1);
    }, []);

    const handleChangeNumberClick = useCallback((rowData) => {
        const changeNumber = rowData["Change Number"];
        if (changeNumber) {
            window.open(`/change_details?changeNumber=${encodeURIComponent(changeNumber)}`, '_blank');
        }
    }, []);

    const handleCiInfoClick = useCallback((rowData, e) => {
        e?.stopPropagation();
        if (onOpenDrawer) {
            onOpenDrawer('ci_info', rowData["Configuration Item"], rowData["Category"] || '');
        }
    }, [onOpenDrawer]);

    const handleDownloadClick = useCallback(() => {
        const headers = ["Change Number", "Configuration Item", "Location", "Risk", "Short Description", 
                        "Planned Start Date", "Planned End Date", "State", "Type", "Assignment Group"];
        const csvData = filteredData.map(item => [
            item["Change Number"], item["Configuration Item"], item["Location"], item["Risk"],
            item["Short Description"], item["Planned Start Date"], item["Planned End Date"],
            item["State"], item["Type"], item["Assignment Group"]
        ]);
        const csv = Papa.unparse({ fields: headers, data: csvData });
        const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
        const fileName = `Site_Changes_${DateTime.now().toFormat('yyyy-MM-dd_HH-mm-ss')}.csv`;
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = fileName;
        link.click();
    }, [filteredData]);

    // ========================================================================
    // RENDER HELPERS
    // ========================================================================

    const renderNavItem = (isSelected, label, count, onClick, indent = 0, badgeColor = '#0077b2', icon = null) => (
        <div 
            style={{ ...styles.navItem(isSelected), paddingLeft: `${16 + indent}px` }}
            onClick={onClick}
            onMouseEnter={(e) => { if (!isSelected) e.currentTarget.style.backgroundColor = '#f0f0f0'; }}
            onMouseLeave={(e) => { if (!isSelected) e.currentTarget.style.backgroundColor = 'transparent'; }}
        >
            <span style={styles.navItemContent}>
                {icon}
                <span style={styles.navItemLabel}>{label}</span>
            </span>
            <span style={styles.badge(badgeColor)}>{count}</span>
        </div>
    );

    const renderExpandIcon = (isExpanded, onClick) => (
        <span onClick={onClick} style={{ cursor: 'pointer', display: 'flex', alignItems: 'center' }}>
            <McIcon icon={isExpanded ? "chevron-up" : "chevron-down"} size="16" />
        </span>
    );

    const renderRegionalNav = () => (
        <>
            {/* All Locations */}
            {renderNavItem(
                selectedNavItem.type === 'all',
                'All Locations',
                siteTypeFilteredData.length,
                () => selectNavItem('all', 'All Locations'),
                0,
                '#0077b2',
                <McIcon icon="globe" size="16" />
            )}

            {/* Regions */}
            <div style={styles.sectionHeader}>Browse by Region</div>
            {Object.entries(filteredNavigation).sort(([a], [b]) => a.localeCompare(b)).map(([region, regionData]) => (
                <div key={region}>
                    {/* Region Item */}
                    <div 
                        style={styles.navItem(selectedNavItem.type === 'region' && selectedNavItem.region === region)}
                        onClick={() => selectNavItem('region', region, region)}
                        onMouseEnter={(e) => { if (!(selectedNavItem.type === 'region' && selectedNavItem.region === region)) e.currentTarget.style.backgroundColor = '#f0f0f0'; }}
                        onMouseLeave={(e) => { if (!(selectedNavItem.type === 'region' && selectedNavItem.region === region)) e.currentTarget.style.backgroundColor = 'transparent'; }}
                    >
                        <span style={styles.navItemContent}>
                            {renderExpandIcon(expandedRegions.has(region), (e) => toggleRegion(region, e))}
                            <span style={{ ...styles.navItemLabel, fontWeight: '500' }}>{region}</span>
                        </span>
                        <span style={styles.badge('#0077b2')}>{regionData.count}</span>
                    </div>

                    {/* Countries */}
                    {expandedRegions.has(region) && Object.entries(regionData.countries)
                        .sort(([a], [b]) => a.localeCompare(b))
                        .map(([country, countryData]) => (
                            <div key={country}>
                                <div 
                                    style={{ ...styles.navItem(selectedNavItem.type === 'country' && selectedNavItem.country === country), paddingLeft: '32px' }}
                                    onClick={() => selectNavItem('country', country, region, country)}
                                    onMouseEnter={(e) => { if (!(selectedNavItem.type === 'country' && selectedNavItem.country === country)) e.currentTarget.style.backgroundColor = '#f0f0f0'; }}
                                    onMouseLeave={(e) => { if (!(selectedNavItem.type === 'country' && selectedNavItem.country === country)) e.currentTarget.style.backgroundColor = 'transparent'; }}
                                >
                                    <span style={styles.navItemContent}>
                                        {Object.keys(countryData.cities || {}).length > 0
                                            ? renderExpandIcon(expandedCountries.has(`${region}::${country}`), (e) => toggleCountry(region, country, e))
                                            : <span style={{ width: '20px' }} />
                                        }
                                        <span style={styles.navItemLabel}>{country}</span>
                                    </span>
                                    <span style={styles.badge('#42a5f5')}>{countryData.count}</span>
                                </div>

                                {/* Cities */}
                                {expandedCountries.has(`${region}::${country}`) && Object.entries(countryData.cities || {})
                                    .sort(([a], [b]) => a.localeCompare(b))
                                    .map(([city, cityData]) => (
                                        <div key={city}>
                                            <div 
                                                style={{ ...styles.navItem(selectedNavItem.type === 'city' && selectedNavItem.city === city), paddingLeft: '52px', fontSize: '13px' }}
                                                onClick={() => selectNavItem('city', city, region, country, city)}
                                                onMouseEnter={(e) => { if (!(selectedNavItem.type === 'city' && selectedNavItem.city === city)) e.currentTarget.style.backgroundColor = '#f0f0f0'; }}
                                                onMouseLeave={(e) => { if (!(selectedNavItem.type === 'city' && selectedNavItem.city === city)) e.currentTarget.style.backgroundColor = 'transparent'; }}
                                            >
                                                <span style={styles.navItemContent}>
                                                    {Object.keys(cityData.sites || {}).length > 0
                                                        ? renderExpandIcon(expandedCountries.has(`${region}::${country}::${city}`), (e) => toggleCountry(region, country, e, city))
                                                        : <span style={{ width: '20px' }} />
                                                    }
                                                    <span style={styles.navItemLabel}>{city}</span>
                                                </span>
                                                <span style={styles.badge('#66bb6a')}>{cityData.count}</span>
                                            </div>

                                            {/* Sites */}
                                            {expandedCountries.has(`${region}::${country}::${city}`) && Object.entries(cityData.sites || {})
                                                .sort(([a], [b]) => a.localeCompare(b))
                                                .map(([site, siteData]) => (
                                                    <div 
                                                        key={site}
                                                        style={{ ...styles.navItem(selectedNavItem.type === 'site' && selectedNavItem.site === site), paddingLeft: '72px', fontSize: '13px' }}
                                                        onClick={() => selectNavItem('site', site, region, country, city, site)}
                                                        onMouseEnter={(e) => { if (!(selectedNavItem.type === 'site' && selectedNavItem.site === site)) e.currentTarget.style.backgroundColor = '#f0f0f0'; }}
                                                        onMouseLeave={(e) => { if (!(selectedNavItem.type === 'site' && selectedNavItem.site === site)) e.currentTarget.style.backgroundColor = 'transparent'; }}
                                                    >
                                                        <span style={styles.navItemContent}>
                                                            <McIcon icon="pin" size="14" />
                                                            <span style={styles.navItemLabel}>{site}</span>
                                                        </span>
                                                        <span style={styles.badge('#90caf9')}>{siteData.count}</span>
                                                    </div>
                                                ))
                                            }
                                        </div>
                                    ))
                                }
                            </div>
                        ))
                    }
                </div>
            ))}
            
            {Object.keys(filteredNavigation).length === 0 && (
                <div style={{ padding: '20px 16px', textAlign: 'center', color: '#666', fontSize: '14px' }}>
                    No locations found
                </div>
            )}
        </>
    );

    const renderGlobalUnmappedNav = () => (
        <div style={{ padding: '20px 16px' }}>
            <div style={{ textAlign: 'center', color: '#666', fontSize: '14px' }}>
                <McIcon icon={activeScope === 1 ? "globe" : "warning-triangle"} size="32" style={{ marginBottom: '12px', opacity: 0.5 }} />
                <p style={{ margin: 0 }}>
                    {activeScope === 1 
                        ? `${siteTypeFilteredData.length} Global change${siteTypeFilteredData.length !== 1 ? 's' : ''}` 
                        : `${siteTypeFilteredData.length} Unmapped change${siteTypeFilteredData.length !== 1 ? 's' : ''}`
                    }
                </p>
                <p style={{ margin: '8px 0 0', fontSize: '12px', color: '#999' }}>
                    {activeScope === 1 
                        ? 'Changes that apply globally across all locations' 
                        : 'Changes without a mapped location'
                    }
                </p>
            </div>
        </div>
    );

    
    return (
        <>
        <div style={styles.container}>
            {/* Segmented Control and Tabs */}
            <div style={styles.tabBarWrapper}>
                <div style={styles.segmentedControlContainer}>
                    <McSegmentedControl
                        fit="medium"
                        listchange={(e) => setShowSiteView(e.detail.item.value === "site")}
                    >
                        <McSegmentedControlItem
                            selected={!showSiteView}
                            value="all"
                        >
                            All Changes
                        </McSegmentedControlItem>
                        <McSegmentedControlItem
                            selected={showSiteView}
                            value="site"
                        >
                            Site Level
                        </McSegmentedControlItem>
                    </McSegmentedControl>
                </div>
                <div style={styles.tabBarContent}>
                    <McTabBar className = "relation-types-tab-bar" tabchange={handleScopeTabChange} currentindex={activeScope}>
                        {SCOPE_TABS.map((tab, index) => (
                            <McTab key={tab} slot="tab" label={tab} />
                        ))}
                    </McTabBar>
                </div>
            </div>

            {/* Filters Bar - Below Tabs */}
            <div style={styles.topBar}>
                <div style={styles.leftSection}>
                    <FilterButton
                        columns={FILTER_COLUMNS}
                        data={normalizedData || []}
                        onApply={handleFilterApply}
                        activeFilters={activeFilters}
                        setActiveFilters={setActiveFilters}
                    />
                    {activeScope === 0 && (
                        <>
                            <McInput
                                placeholder="Search locations..."
                                value={siteSearch}
                                label=""
                                input={handleSearchChange}
                                fit="small"
                                clearbutton
                                clearbuttonclick={() => setSiteSearch('')}
                                style={{ width: '220px' }}
                            >
                                <McIcon icon="magnifying-glass" slot="prefix" />
                            </McInput>
                            <McSelect
                                label="Site Type"
                                value={selectedSiteType}
                                optionselected={handleSiteTypeChange}
                                fit="small"
                                hiddenlabel
                                style={{ width: '160px' }}
                            >
                                {SITE_TYPES.map(type => (
                                    <McOption key={type.value} value={type.value}>{type.label}</McOption>
                                ))}
                            </McSelect>
                        </>
                    )}
                </div>
                <div style={styles.middleSection}>
                    <span>Total: {filteredData.length}</span>
                </div>
                <div style={styles.rightSection}>
                    <McButton
                        label="Download"
                        appearance="primary"
                        variant="filled"
                        fit="small"
                        click={handleDownloadClick}
                        trailingicon="tray-arrow-down"
                    />
                </div>
            </div>

            <div style={styles.mainContent}>
                {/* Left Sidebar - Only shown for Regional scope */}
                {activeScope === 0 && (
                    <div style={styles.sidebar}>
                        {/* Navigation */}
                        <div style={styles.sidebarNav}>
                            {renderRegionalNav()}
                        </div>
                    </div>
                )}

                {/* Right Content Panel */}
                <div style={styles.contentPanel}>
                    {/* Table */}
                    <div style={styles.tableContainer}>
                        <McTable
                            data={transformedData}
                            columns={TABLE_COLUMNS}
                            datakey="id"
                            headersticky
                            fit="small"
                            sortmanual
                            sortchange={handleTableSorted}
                            sortdefaultcolumnid={sortColumn}
                            sortdefaultdirection={sortDirection}
                            resetscrollonpagechange={false}
                            stateslotheight="200px"
                        >
                            {loading && (
                                <div slot="state" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                                    <McLoadingIndicator appearance="primary" />
                                </div>
                            )}
                            {transformedData.length === 0 && !loading && (
                                <div slot="state" style={styles.emptyState}>
                                    No changes found
                                </div>
                            )}
                            {transformedData.map((row) => (
                                <React.Fragment key={row.id}>
                                    <div slot={`${row.id}_changeNumber`}>
                                        <span
                                            onClick={() => handleChangeNumberClick(row._originalData)}
                                            style={{ color: "#0077b2", cursor: "pointer" }}
                                        >
                                            {row.changeNumber}
                                        </span>
                                    </div>
                                    <div slot={`${row.id}_configItem`}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                            <span>{row.configItem}</span>
                                            {row._originalData["Category"] === "Application" && (
                                                <McTooltip position="top-center">
                                                    <span 
                                                        slot="trigger" 
                                                        style={{ cursor: 'pointer', display: 'flex' }}
                                                        onClick={(e) => handleCiInfoClick(row._originalData, e)}
                                                    >
                                                        <McIcon icon="info-circle" size="16" />
                                                    </span>
                                                    <span>Show CI Info</span>
                                                </McTooltip>
                                            )}
                                        </div>
                                    </div>
                                    <div slot={`${row.id}_risk`}>
                                        <span style={{ color: getRiskColor(row.risk), fontWeight: '500' }}>{row.risk}</span>
                                    </div>
                                    <div slot={`${row.id}_state`}>
                                        <span style={{ color: getStateColor(row.state), fontWeight: '500' }}>{row.state}</span>
                                    </div>
                                    <div slot={`${row.id}_shortDescription`}>
                                        <span style={{ maxWidth: "400px", wordBreak: "break-word", fontSize: "14px" }}>
                                            {row.shortDescription}
                                        </span>
                                    </div>
                                </React.Fragment>
                            ))}
                        </McTable>
                    </div>

                    {/* Pagination */}
                    {filteredData.length > RECORDS_PER_PAGE && (
                        <div style={styles.pagination}>
                            <McPagination
                                currentpage={currentPage}
                                totalpages={totalPages}
                                pagechange={handlePageChange}
                            />
                        </div>
                    )}
                </div>
            </div>
        </div>
        </>
    );
}
